import { Trans } from 'next-i18next';

import { CartShippingModel } from '@/models';
import { CallToActionKind } from '@/types';
import CallToActionContainer from '@/components/common/callToAction/CallToActionContainer';
import CardDS from '@/components/common/design-system/Card.ds';
import Divider from '@/components/common/Divider';
import Icon from '@/components/common/iconedLinks/Icon';
import MillesimaPrice from '@/components/common/price/MillesimaPrice';
import RadioButton from '@/components/common/RadioButton';
import Tile from '@/components/common/Tile';
import { ProductsPerAvailableTypeModal } from '@/components/checkout/shipMode/ProductsPerAvailableTypeModal';
import { useScreenSizeContext } from '@/components/ScreenSize.context';
import ShippingContent from '@/components/checkout/shipMode/DeliverySelector/ShippingContent';

import { useShippingList } from '@/components/checkout/shipMode/useShippingList';

import styles from './ShippingList.module.scss';
import { PRICE } from '@/constants/price';
import { URLS } from '@/constants/urls';

type UnitType = 'M' | 'W' | 'H' | 'Y' | 'D';

type EnhancedCartShippingModel = CartShippingModel & {deliveryTime:{date?: string, time?: number, unit?: UnitType}};
type ShippingArrayFromMap = ['L' | 'PA' | 'P', EnhancedCartShippingModel[]];

export const ShippingList = () => {
  const presenter = useShippingList();
  const isMobile = useScreenSizeContext().isMobile;

  function shippingListComponent(availableType: 'L' | 'PA' | 'P', shippingList: EnhancedCartShippingModel[]) {
    const shippingId = presenter.getSelectedShippingIdByAvailableType[availableType];
    if (availableType === 'P' && shippingList.length < 2) {
      return (
        <>
          <div className={styles['primeur-no-action']}>
            <p className={styles['primeur-no-action-title']}>{presenter.translate('checkout:shipMode.noAction.title')}</p>
            <p className={styles['primeur-no-action-desc']}>{presenter.translate('checkout:shipMode.noAction.desc')}</p>
          </div>
          {!presenter.userWantsToSeeDeliveryConditions && (
            <CallToActionContainer kind={CallToActionKind.GHOST} onClick={presenter.showDeliveryConditions}>
              {presenter.translate('checkout:shipMode.delivery.seeConditions')}
            </CallToActionContainer>
          )}
          {presenter.userWantsToSeeDeliveryConditions && (
            <p>{presenter.translate('checkout:shipMode.delivery.conditions')}</p>
          )}
        </>
      );
    }
    return (
      <div className={styles.content}>
        {shippingList.map((shipping) => (
          <Tile
            dataRbf="shipping-tile"
            onClick={() => presenter.updateShipping({ shippingId: shipping._id, availableType })}
            key={shipping._id}
            informativeBanner={shipping.informativeBanner}
            leadingContent={<RadioButton id={shipping._id} isChecked={shipping._id === shippingId} />}
            trailingContent={
              <MillesimaPrice
                value={shipping.isOffered ? PRICE.FREE : shipping.price}
                className={{ value: `${styles.price} ${shipping.isOffered && styles.free}` }}
              />
            }
            selected={shipping._id === shippingId}
            content={{
              label: shipping.title,
              paragraph: shipping.desc,
              component:
                  <>
                    <Divider spacing={16} />
                    <ShippingContent
                      shipping={shipping}
                      onUpdate={
                        (modifiedShipping) => presenter.updateShipping({
                          shippingId: shipping._id, availableType, modifiedShipping
                        })
                      }
                    />
                  </>
            }}
          />
        ))}
      </div>
    );
  }

  return (
    <>
      {presenter.productPerAvailableTypeModalIsOpened && (
        <ProductsPerAvailableTypeModal />
      )}
      <section className={styles.container}>
        {presenter.shippingList.map(
          ([availableType, list]: ShippingArrayFromMap, index: number) => {
            const shipmentNumber = `${index + 1}/${presenter.shippingList.length}`;
            const orderBeforeDate = presenter.getOrderBeforeDate(availableType, list);
            const shippingDate = presenter.getShippingDate(availableType, list);

            return (
              <div key={availableType} className={styles['card-container']}>
                <CardDS
                  className={styles['shipping-list-container']}
                  firstContent={
                    <div className={styles['first-content']}>
                      <Tile
                        borderless subParagraphPosition="top" padding="none"
                        leadingContent={!isMobile && <Icon src={URLS.DELIVERY_CIRCLE} alt="shipping" width={48} height={48} />}
                        trailingContent={
                          <CallToActionContainer
                            kind={CallToActionKind.GHOST}
                            onClick={() =>
                              presenter.showProductsPerAvailableType(
                                availableType, shipmentNumber,
                                orderBeforeDate, shippingDate
                              )
                            }
                          >
                            {presenter.translate('checkout:shipMode.article')}
                          </CallToActionContainer>
                        }
                        content={{
                          label: `${presenter.translate('checkout:cart.shipment')} ${shipmentNumber}`,
                          paragraph: availableType !== 'P' &&
                            ((availableType === 'L' && shippingDate) ||
                              (availableType === 'PA' && orderBeforeDate !== '1 January 1970')
                            ) && <Trans
                            i18nKey={`checkout:shipMode.delivery.${availableType}`}
                            values={{
                              deliveryDate: shippingDate,
                              orderDate: orderBeforeDate
                            }}
                            components={{
                              dateStyle: <span className={styles.date} />
                            }}
                          />,
                          subParagraph: presenter.getLabel(availableType)
                        }}
                      />
                    </div>
                  }
                />
                <CardDS
                  className={styles['shipping-list-container']}
                  content={shippingListComponent(availableType as 'L' | 'PA' | 'P', list)}
                />
              </div>
            );
          })}
      </section>
    </>
  );
};
